import React from "react";
import { Link } from "react-router-dom";
import { useWallet } from "@/context/WalletContext";
import { usePolling } from "@/components/croc/hooks";
import { Panel, Pill } from "@/components/croc/ui";
import { fmtUsd, fmtNum } from "@/lib/format";
import api from "@/lib/api";
import { capRatio, CAP_WARN_RATIO } from "@/components/croc/CapAlert";
import { Layers, Gift, Clock, Coins, TrendingUp, ArrowRight } from "lucide-react";

function daysLeft(end) {
  if (!end) return 0;
  return Math.max(0, Math.ceil((new Date(end) - Date.now()) / (1000 * 60 * 60 * 24)));
}

const PRED_TONE = { earned: "emerald", in_progress: "gold", forfeited: "danger" };
const PRED_LABEL = { earned: "Bonus earned", in_progress: "Answer to earn", forfeited: "Forfeited today" };

export const PackageCards = () => {
  const { user, address } = useWallet();
  const { data: pred } = usePolling(() => (address ? api.predictionPackages(address) : Promise.resolve(null)), [address], 20000);

  const stakes = (user?.stakes ?? []).filter((s) => s.active && s.status === "active");
  const predByStake = {};
  (pred?.packages ?? []).forEach((p) => { predByStake[p.stake_id] = p; });

  return (
    <Panel className="mt-6" data-testid="package-dashboard">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Layers className="w-4 h-4 emerald-text" />
          <span className="font-display font-bold text-lg text-white">My Packages</span>
        </div>
        <span className="text-xs text-muted-foreground">{stakes.length} active</span>
      </div>

      {stakes.length === 0 ? (
        <div className="text-center py-10 text-muted-foreground text-sm" data-testid="package-empty">
          No active packages yet. Buy &amp; auto-stake to open your first package.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {stakes.map((s, i) => {
            const daily = (s.principal_usd || 0) * (s.daily_rate || 0);
            const p = predByStake[s.id];
            const dl = daysLeft(s.end);
            return (
              <div key={s.id} className="rounded-2xl border border-white/10 bg-[#0B120E] p-4 hover:border-[#1CFD4C]/40 transition-colors" data-testid={`package-card-${i}`}>
                <div className="flex items-center justify-between mb-3">
                  <Pill tone="emerald">{s.tier_name}</Pill>
                  <span className="text-[10px] text-muted-foreground font-num flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {dl}d left
                  </span>
                </div>

                <div className="text-2xl font-num font-black text-white">{fmtUsd(s.principal_usd)}</div>
                <div className="text-[11px] text-muted-foreground font-num flex items-center gap-1 mt-0.5">
                  <Coins className="w-3 h-3" /> {fmtNum(s.croc_amount, 2)} CROC staked
                </div>

                <div className="grid grid-cols-2 gap-2 mt-3">
                  <div className="rounded-lg border border-white/10 bg-[#090E0B] px-3 py-2">
                    <div className="text-[9px] uppercase tracking-widest text-muted-foreground">Daily ROI</div>
                    <div className="font-num font-bold emerald-text text-sm flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" /> {fmtUsd(daily)}
                    </div>
                    <div className="text-[9px] text-muted-foreground">{fmtNum((s.daily_rate || 0) * 100, 2)}%/day</div>
                  </div>
                  <div className="rounded-lg border border-white/10 bg-[#090E0B] px-3 py-2">
                    <div className="text-[9px] uppercase tracking-widest text-muted-foreground">Accrued</div>
                    <div className="font-num font-bold gold-text text-sm">{fmtUsd(s.accrued)}</div>
                    <div className="text-[9px] text-muted-foreground">to date</div>
                  </div>
                </div>

                {s.earning_cap_usd > 0 && (() => {
                  const warn = capRatio(s) >= CAP_WARN_RATIO;
                  return (
                  <div className={`mt-3 rounded-lg border px-3 py-2 ${warn ? "border-[#D4AF37]/40 bg-[#D4AF37]/[0.06]" : "border-white/10 bg-[#090E0B]"}`} data-testid={`package-cap-${i}`}>
                    <div className="flex items-center justify-between text-[10px]">
                      <span className="uppercase tracking-widest text-muted-foreground">Earning Cap · {fmtNum(s.earning_cap_usd / s.principal_usd, 0)}x</span>
                      <span className="font-num text-white" data-testid={`package-cap-value-${i}`}>
                        {fmtUsd(s.cap_earned)} / {fmtUsd(s.earning_cap_usd)}
                      </span>
                    </div>
                    <div className="mt-1.5 h-1.5 rounded-full bg-white/10 overflow-hidden">
                      <div className={`h-full transition-[width] duration-500 ${warn ? "bg-[#D4AF37]" : "bg-[#1CFD4C]"}`}
                        style={{ width: `${Math.min(100, capRatio(s) * 100)}%` }} />
                    </div>
                    {warn && (
                      <div className="mt-1.5 text-[10px] gold-text" data-testid={`package-cap-warning-${i}`}>
                        {Math.round(capRatio(s) * 100)}% used · {fmtUsd(Math.max(0, s.earning_cap_usd - s.cap_earned))} left. Re-stake to keep earning
                      </div>
                    )}
                  </div>
                  );
                })()}

                <div className="mt-3 flex items-center justify-between rounded-lg border border-white/10 bg-[#090E0B] px-3 py-2">
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                    <Gift className="w-3 h-3" /> Prediction
                  </span>
                  {p ? (
                    <Pill tone={PRED_TONE[p.bonus_status] || "gold"} className="text-[10px]" data-testid={`package-pred-${i}`}>
                      {PRED_LABEL[p.bonus_status] || "—"} · {p.answered}/{p.required_per_day}
                    </Pill>
                  ) : (
                    <span className="text-[10px] text-muted-foreground">—</span>
                  )}
                </div>

                {s.can_claim_principal && (
                  <div className="mt-2 text-[10px] text-[#1CFD4C] text-center">Principal ready to claim →</div>
                )}

                <Link
                  to={`/prediction?pkg=${s.id}`}
                  data-testid={`package-pred-link-${i}`}
                  className="mt-3 flex items-center justify-center gap-1.5 rounded-xl border border-white/10 bg-[#090E0B] py-2 text-xs font-medium text-muted-foreground hover:border-[#1CFD4C]/50 hover:text-white transition-colors"
                >
                  Answer predictions <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            );
          })}
        </div>
      )}
    </Panel>
  );
};
